// The template composer's draft shape, its constants, and a client-side mirror of the server's
// validation.
//
// THE SERVER REMAINS THE AUTHORITY. backend/src/services/templateValidation.js re-validates every
// submission unconditionally, and this file exists purely so the operator finds out before
// committing rather than through a 400 — the same argument templateVariables.ts makes for its own
// mirroring, and the same discipline: every rule here must match a rule there.
//
// The numbers live in one object so a drift between the two copies is a single visible list rather
// than scattered magic numbers.

import { extractVariableIndices } from './templateVariables';

/** Meta's ceilings. Mirrors LIMITS in backend/src/services/templateValidation.js. */
export const TEMPLATE_LIMITS = {
  NAME: 512,
  BODY: 1024,
  HEADER_TEXT: 60,
  FOOTER: 60,
  BUTTON_TEXT: 25,
  BUTTON_URL: 2000,
  BUTTONS_TOTAL: 10,
  BUTTONS_URL: 2,
  BUTTONS_PHONE: 1,
  /** The workspace prefix Meta actually stores: 9 characters plus an underscore. */
  NAME_PREFIX_COST: 10,
} as const;

/** Only these two can be created here. Authentication templates are a different object entirely. */
export const TEMPLATE_CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', hint: 'Promotions, offers, new listings.' },
  { value: 'UTILITY', label: 'Utility', hint: 'Updates about something already agreed.' },
] as const;

export const TEMPLATE_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ar_EG', label: 'Arabic (Egypt)' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
] as const;

export const HEADER_TYPES = [
  { value: 'NONE', label: 'No header' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
] as const;

export const MEDIA_HEADER_TYPES = ['IMAGE', 'VIDEO', 'DOCUMENT'];

/**
 * The contact fields a body variable can be filled from when the message is actually sent. Mirrors
 * MAPPABLE_CONTACT_FIELDS in backend/src/services/templateVariableMapping.js — the backend refuses
 * a mapping to any field not on its list.
 */
export const CONTACT_FIELDS = [
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'propertyInterest', label: 'Property Interest' },
  { value: 'budget', label: 'Budget' },
  // "1,500,000 AED". `budget` above stays the bare amount, for templates that write the currency
  // into their own text.
  { value: 'budgetWithCurrency', label: 'Budget (with currency)' },
  { value: 'locationPreference', label: 'Location' },
  // Sent as the country's name in the template's language, never as the stored code.
  { value: 'currentCountry', label: 'Current Country' },
  { value: 'notes', label: 'Notes' },
  { value: 'source', label: 'Source' },
];

/** What WhatsApp accepts per header format. Mirrors the map already used for test sends. */
export const SAMPLE_ACCEPT: Record<string, string> = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};

export type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';

export interface DraftButton {
  type: ButtonType;
  text: string;
  url?: string;
  phoneNumber?: string;
  urlSample?: string;
  /** The mandatory opt-out row: its label is picked from a fixed list and it cannot be removed. */
  optOut?: boolean;
}

// --- the mandatory opt-out button --------------------------------------------------------------
//
// Every template authored here carries a quick reply that unsubscribes whoever taps it. The tap
// comes back as an inbound reply whose text is the label, and the server's opt-out keyword list is
// built from this same list — so a label can only be one of these.

/** Mirrors OPT_OUT_BUTTON_LABELS in backend/src/services/templateValidation.js. */
export const OPT_OUT_BUTTON_LABELS = {
  en: ['Not Interested', 'Unsubscribe', 'Opt out'],
  ar: ['غير مهتم', 'إلغاء الاشتراك', 'إيقاف الرسائل'],
} as const;

/** Arabic labels for `ar` and `ar_*`, English for every other language. */
export const optOutLabelsFor = (language: string): readonly string[] =>
  (/^ar(_|$)/i.test(language ?? '') ? OPT_OUT_BUTTON_LABELS.ar : OPT_OUT_BUTTON_LABELS.en);

const isOptOutLabel = (text: string, language: string) =>
  optOutLabelsFor(language).some((label) => label.toLowerCase() === (text ?? '').trim().toLowerCase());

export const optOutButton = (language: string): DraftButton => ({
  type: 'QUICK_REPLY',
  text: optOutLabelsFor(language)[0],
  optOut: true,
});

/**
 * Carry the opt-out label across a language change, keeping its position in the list — so
 * "Unsubscribe" becomes "إلغاء الاشتراك" rather than resetting to the first choice.
 */
export const relabelOptOut = (buttons: DraftButton[], from: string, to: string): DraftButton[] =>
  buttons.map((b) => {
    if (!b.optOut) return b;
    const position = Math.max(0, optOutLabelsFor(from).findIndex((l) => l === b.text));
    return { ...b, text: optOutLabelsFor(to)[position] };
  });

/**
 * Give an existing template's buttons their opt-out row, for editing.
 *
 * A quick reply already carrying one of the labels becomes that row. A template without one gets a
 * new row inside the quick-reply group — after the last quick reply when they lead, at the end
 * otherwise — so the grouping rule still holds for a list that satisfied it before.
 */
export const withOptOutButton = (buttons: DraftButton[], language: string): DraftButton[] => {
  const existing = buttons.findIndex((b) => b.type === 'QUICK_REPLY' && isOptOutLabel(b.text, language));
  if (existing !== -1) {
    return buttons.map((b, i) => (i === existing ? { ...b, optOut: true } : b));
  }
  const quickRepliesLead = buttons[0]?.type === 'QUICK_REPLY';
  const lastQuickReply = buttons.map((b) => b.type).lastIndexOf('QUICK_REPLY');
  const at = quickRepliesLead ? lastQuickReply + 1 : buttons.length;
  return [...buttons.slice(0, at), optOutButton(language), ...buttons.slice(at)];
};

export interface TemplateDraft {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY';
  headerType: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerContent: string;
  headerSample: string;
  /** From POST /media-sample — an opaque Meta upload handle, held until submit. */
  headerHandle: string | null;
  /** The uploaded file's name, for display only. */
  headerFilename: string | null;
  body: string;
  /** '1' -> 'Ahmed'. Doubles as the preview's variable labels. */
  bodySamples: Record<string, string>;
  /** '1' -> 'name'. The link no provider has a concept of. */
  variableMapping: Record<string, string>;
  footer: string;
  buttons: DraftButton[];
  /**
   * The generated media this template is being made from ("Use in a template"). Campaigns using
   * the template start with it as their creative. Null for a template written from scratch.
   */
  campaignMediaId: string | null;
  /**
   * Which of that media's photos or videos is the header. Its handle is fetched from the server
   * when it is picked (POST /media-sample/from-asset), so `headerHandle` is still what Meta needs.
   */
  headerAssetUid: string | null;
  /** A preview of that asset, for display only — like `headerFilename`, never sent. */
  headerPreviewUrl: string | null;
}

/**
 * One of a media item's generated photos or videos, offered as a template header. `usable` is
 * false for a type WhatsApp refuses (a WebP, say) — offered greyed out with the reason, rather
 * than hidden, so nobody wonders where their picture went.
 */
export interface TemplateMediaAsset {
  uid: string;
  kind: 'photo' | 'video';
  label: string;
  url: string | null;
  mime: string | null;
  /** Part of the media's final selection in the builder — what the user already said they want. */
  selected: boolean;
  usable: boolean;
  reason?: string;
}

export const emptyDraft: TemplateDraft = {
  name: '',
  language: 'en',
  category: 'MARKETING',
  headerType: 'NONE',
  headerContent: '',
  headerSample: '',
  headerHandle: null,
  headerFilename: null,
  body: '',
  bodySamples: {},
  variableMapping: {},
  footer: '',
  buttons: [optOutButton('en')],
  campaignMediaId: null,
  headerAssetUid: null,
  headerPreviewUrl: null,
};

/** Header types a generated asset can fill: we generate photos and videos, never documents. */
export const ASSET_KIND_FOR_HEADER: Record<string, 'photo' | 'video'> = { IMAGE: 'photo', VIDEO: 'video' };

export interface DraftError {
  field: string;
  message: string;
}

const VARIABLE_RE = /\{\{(\d+)\}\}/g;
const hasVariable = (text: string) => /\{\{\d+\}\}/.test(text ?? '');
const badSampleWhitespace = (value: string) => /[\n\r\t]/.test(value) || / {4,}/.test(value);

/** Any {{…}} that is not a numbered variable. Mirrors hasUnnumberedPlaceholder on the server. */
const hasUnnumberedPlaceholder = (text: string) => /\{\{(?!\d+\}\})[^{}]*\}\}/.test(text ?? '');
const UNNUMBERED_MESSAGE = 'Use numbered variables like {{1}} here.';

// --- named variables ---------------------------------------------------------------------------
//
// Authors type {{name}} or {{budget}}; WhatsApp, the sender and every mapping in this product only
// know {{1}}, {{2}} …. So the composer accepts both and converts at the edge: each distinct variable
// is numbered by first appearance, and a name that matches a contact field is mapped to it. The
// body the author sees keeps their names; what is validated, previewed and submitted is positional.
//
// Meta's own named-parameter format is deliberately not used. It would change every send, sync and
// mapping path, and the sending provider's support for it is unknown.

/** {{name}} or {{1}}, tolerating spaces inside the braces. */
const TOKEN_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*|\d+)\s*\}\}/g;

/** Named variables are case-insensitive, so {{Name}} and {{name}} are one variable. */
const tokenKey = (raw: string) => (/^\d+$/.test(raw) ? raw : raw.toLowerCase());

const squash = (value: string) => value.toLowerCase().replace(/[\s_-]+/g, '');

// Spellings beyond a field's own value and label. Compared squashed, so {{Full_Name}},
// {{full-name}} and {{fullname}} all land on the same key.
const FIELD_ALIASES: Record<string, string> = {
  fullname: 'name',
  firstname: 'name',
  clientname: 'name',
  customername: 'name',
  mobile: 'phone',
  phonenumber: 'phone',
  whatsapp: 'phone',
  emailaddress: 'email',
  property: 'propertyInterest',
  propertytype: 'propertyInterest',
  interest: 'propertyInterest',
  area: 'locationPreference',
  city: 'locationPreference',
  country: 'currentCountry',
  note: 'notes',
};

/** The contact field a named variable refers to, or null when it names none of them. */
export const contactFieldForToken = (token: string): string | null => {
  const key = squash(token);
  if (!key || /^\d+$/.test(key)) return null;
  const field = CONTACT_FIELDS.find((f) => squash(f.value) === key || squash(f.label) === key);
  return field?.value ?? FIELD_ALIASES[key] ?? null;
};

export interface DraftVariable {
  /** What the author typed between the braces: 'name', or '1'. Keys the draft's samples and mapping. */
  token: string;
  /** The positional index it is submitted as. */
  index: string;
}

/**
 * Convert the author's body to the positional form WhatsApp takes.
 *
 * The draft keys `bodySamples` and `variableMapping` by TOKEN, so a sample typed against {{name}}
 * stays with it however the variables are reordered. The returned draft is keyed by INDEX, and is the
 * one to validate, preview and submit. A mapping key holding '' is the author choosing "Not mapped",
 * which is why it is not auto-filled again from the token's name.
 */
export const normalizeDraftVariables = (
  draft: TemplateDraft,
): { draft: TemplateDraft; variables: DraftVariable[] } => {
  const indexOf = new Map<string, string>();
  const body = (draft.body ?? '').replace(TOKEN_RE, (_match, raw: string) => {
    const token = tokenKey(raw);
    if (!indexOf.has(token)) indexOf.set(token, String(indexOf.size + 1));
    return `{{${indexOf.get(token)}}}`;
  });

  const variables = [...indexOf].map(([token, index]) => ({ token, index }));
  const samples = draft.bodySamples ?? {};
  const mapping = draft.variableMapping ?? {};
  const bodySamples: Record<string, string> = {};
  const variableMapping: Record<string, string> = {};

  for (const { token, index } of variables) {
    if (samples[token] !== undefined) bodySamples[index] = samples[token];
    const field = token in mapping ? mapping[token] : contactFieldForToken(token);
    if (field) variableMapping[index] = field;
  }

  return { draft: { ...draft, body, bodySamples, variableMapping }, variables };
};

/**
 * Turn free text into a valid WhatsApp template name as the user types.
 *
 * Meta's rule is `^[a-z][a-z0-9_]*$`, so this lowercases, turns runs of anything else into single
 * underscores, and drops a leading digit or underscore. Applied on change rather than on blur so
 * the name field can never hold something that will be rejected.
 */
export const slugifyTemplateName = (value: string): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+/g, '_')
    .slice(0, TEMPLATE_LIMITS.NAME - TEMPLATE_LIMITS.NAME_PREFIX_COST);

/**
 * Insert a variable at the cursor, numbered one past the highest already present.
 *
 * Named variables count towards that number, so inserting next to a lone {{name}} gives {{2}} rather
 * than a {{1}} that would be renumbered on submit anyway.
 *
 * Returns the new body and where the caret should land. Appending to the end — what the old helper
 * on this page did — is wrong for a composer: the variable almost always belongs mid-sentence, and
 * a body that ENDS with a variable is rejected by Meta outright.
 */
export const insertVariableAt = (
  body: string,
  cursor: number,
): { body: string; cursor: number; index: number } => {
  const existing = extractVariableIndices(body).map(Number);
  const tokens = new Set([...body.matchAll(TOKEN_RE)].map((m) => tokenKey(m[1])));
  const next = Math.max(tokens.size, ...existing, 0) + 1;
  const token = `{{${next}}}`;
  const at = Math.max(0, Math.min(cursor ?? body.length, body.length));
  return {
    body: `${body.slice(0, at)}${token}${body.slice(at)}`,
    cursor: at + token.length,
    index: next,
  };
};

/**
 * Validate a draft. Mirrors validateTemplateDraft on the server.
 *
 * Returns every problem rather than the first, so the composer annotates all offending fields at
 * once — the same contract the server's 400 carries in `body.errors`.
 */
export const validateDraft = (
  draft: TemplateDraft,
  { canUploadSamples = true }: { canUploadSamples?: boolean } = {},
): DraftError[] => {
  const errors: DraftError[] = [];
  const add = (field: string, message: string) => errors.push({ field, message });

  // --- name ---
  const name = (draft.name ?? '').trim();
  if (!name) add('name', 'A template name is required.');
  else if (!/^[a-z][a-z0-9_]*$/.test(name)) {
    add('name', 'Use lowercase letters, numbers and underscores, starting with a letter.');
  } else if (name.length + TEMPLATE_LIMITS.NAME_PREFIX_COST > TEMPLATE_LIMITS.NAME) {
    add('name', 'That name is too long.');
  }

  // --- header ---
  if (draft.headerType === 'TEXT') {
    const text = (draft.headerContent ?? '').trim();
    if (!text) add('headerContent', 'Add header text, or choose "No header".');
    else {
      if (text.length > TEMPLATE_LIMITS.HEADER_TEXT) {
        add('headerContent', `Headers must be ${TEMPLATE_LIMITS.HEADER_TEXT} characters or fewer.`);
      }
      // Only the body is converted from {{name}} to {{1}}; a header variable has no contact field.
      if (hasUnnumberedPlaceholder(text)) add('headerContent', 'A header variable must be {{1}}.');
      const vars = [...text.matchAll(VARIABLE_RE)].map((m) => m[1]);
      if (vars.length > 1) add('headerContent', 'A header can hold at most one variable.');
      else if (vars.length === 1) {
        if (vars[0] !== '1') add('headerContent', 'A header variable must be {{1}}.');
        const sample = (draft.headerSample ?? '').trim();
        if (!sample) add('headerSample', 'Give an example value for the header variable.');
        else if (badSampleWhitespace(sample)) {
          add('headerSample', 'Examples cannot contain line breaks, tabs, or runs of spaces.');
        }
      }
    }
  } else if (MEDIA_HEADER_TYPES.includes(draft.headerType)) {
    if (!canUploadSamples) {
      add('headerType', 'Media headers are unavailable on this server. Use a text header or none.');
    } else if (!draft.headerHandle) {
      add('headerSample', `Upload a sample ${draft.headerType.toLowerCase()} for the header.`);
    }
  }

  // --- body ---
  const body = draft.body ?? '';
  if (!body.trim()) add('body', 'A message body is required.');
  else {
    if (body.length > TEMPLATE_LIMITS.BODY) {
      add('body', `The body must be ${TEMPLATE_LIMITS.BODY} characters or fewer. It is ${body.length}.`);
    }
    // Reached only by what normalizeDraftVariables could not read as a variable: {{full name}}, {{}}.
    if (hasUnnumberedPlaceholder(body)) {
      add('body', `${UNNUMBERED_MESSAGE} Variable names use letters, numbers and underscores, like {{full_name}}.`);
    }

    const indices = extractVariableIndices(body);
    indices.forEach((index, position) => {
      if (Number(index) !== position + 1) {
        add('body', `Variables must run in order from {{1}}. Found {{${index}}} where {{${position + 1}}} was expected.`);
      }
    });

    const trimmed = body.trim();
    if (/^\{\{\d+\}\}/.test(trimmed)) add('body', 'The body cannot start with a variable.');
    if (/\{\{\d+\}\}$/.test(trimmed)) add('body', 'The body cannot end with a variable.');
    if (/\{\{\d+\}\}\s*\{\{\d+\}\}/.test(body)) {
      add('body', 'Two variables cannot sit next to each other. Put some text between them.');
    }

    for (const index of indices) {
      const sample = (draft.bodySamples?.[index] ?? '').trim();
      // Not "for {{n}}": the row this sits under is labelled with whatever the author typed.
      if (!sample) add(`bodySamples.${index}`, 'Give an example value for this variable.');
      else if (badSampleWhitespace(sample)) {
        add(`bodySamples.${index}`, 'Examples cannot contain line breaks, tabs, or runs of spaces.');
      }
    }
  }

  // --- footer ---
  const footer = (draft.footer ?? '').trim();
  if (footer) {
    if (footer.length > TEMPLATE_LIMITS.FOOTER) {
      add('footer', `Footers must be ${TEMPLATE_LIMITS.FOOTER} characters or fewer.`);
    }
    if (hasVariable(footer) || hasUnnumberedPlaceholder(footer)) {
      add('footer', 'A footer cannot contain variables.');
    }
  }

  // --- buttons ---
  const buttons = draft.buttons ?? [];
  if (buttons.length > TEMPLATE_LIMITS.BUTTONS_TOTAL) {
    add('buttons', `At most ${TEMPLATE_LIMITS.BUTTONS_TOTAL} buttons.`);
  }

  const seen = new Set<string>();
  let urls = 0;
  let phones = 0;

  buttons.forEach((button, i) => {
    const text = (button.text ?? '').trim();
    if (!text) add(`buttons.${i}.text`, 'A button needs a label.');
    else {
      if (text.length > TEMPLATE_LIMITS.BUTTON_TEXT) {
        add(`buttons.${i}.text`, `Labels must be ${TEMPLATE_LIMITS.BUTTON_TEXT} characters or fewer.`);
      }
      if (seen.has(text.toLowerCase())) {
        add(`buttons.${i}.text`, `Two buttons cannot share the label "${text}".`);
      }
      seen.add(text.toLowerCase());
    }

    if (button.type === 'URL') {
      urls += 1;
      const url = (button.url ?? '').trim();
      if (!url) add(`buttons.${i}.url`, 'A link button needs a URL.');
      else {
        if (url.length > TEMPLATE_LIMITS.BUTTON_URL) {
          add(`buttons.${i}.url`, 'That URL is too long.');
        }
        if (!/^https:\/\//i.test(url)) add(`buttons.${i}.url`, 'The URL must start with https://.');
        if (hasUnnumberedPlaceholder(url)) add(`buttons.${i}.url`, UNNUMBERED_MESSAGE);
        const vars = [...url.matchAll(VARIABLE_RE)];
        if (vars.length > 1) add(`buttons.${i}.url`, 'A link can hold at most one variable.');
        else if (vars.length === 1) {
          if (!/\{\{\d+\}\}$/.test(url)) {
            add(`buttons.${i}.url`, 'A URL variable must sit at the very end of the link.');
          }
          if (!(button.urlSample ?? '').trim()) {
            add(`buttons.${i}.urlSample`, 'Give an example link.');
          }
        }
      }
    } else if (button.type === 'PHONE_NUMBER') {
      phones += 1;
      const phone = (button.phoneNumber ?? '').trim();
      if (!phone) add(`buttons.${i}.phoneNumber`, 'A call button needs a phone number.');
      else if (!/^\+[1-9]\d{6,17}$/.test(phone)) {
        add(`buttons.${i}.phoneNumber`, 'Use international format, e.g. +201234567890.');
      }
    }
  });

  if (urls > TEMPLATE_LIMITS.BUTTONS_URL) {
    add('buttons', `At most ${TEMPLATE_LIMITS.BUTTONS_URL} link buttons.`);
  }
  if (phones > TEMPLATE_LIMITS.BUTTONS_PHONE) {
    add('buttons', `At most ${TEMPLATE_LIMITS.BUTTONS_PHONE} call button.`);
  }

  if (!buttons.some((b) => b.type === 'QUICK_REPLY' && isOptOutLabel(b.text, draft.language))) {
    const labels = optOutLabelsFor(draft.language);
    add('buttons', `Every template needs an opt-out quick reply: ${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}.`);
  }

  const pattern = buttons.map((b) => (b.type === 'QUICK_REPLY' ? 'q' : 'c')).join('');
  if (pattern.includes('q') && pattern.includes('c') && !/^q+c+$|^c+q+$/.test(pattern)) {
    add('buttons', 'Group the quick replies together, before or after the link and call buttons.');
  }

  return errors;
};

/** Index the error list by field, so each control can render its own message. */
export const errorsByField = (errors: DraftError[]): Record<string, string> =>
  errors.reduce<Record<string, string>>((acc, e) => {
    if (!acc[e.field]) acc[e.field] = e.message;
    return acc;
  }, {});

/** The payload POST /whatsapp-templates expects. Drops the display-only fields. */
export const draftToPayload = (draft: TemplateDraft) => ({
  name: draft.name.trim(),
  language: draft.language,
  category: draft.category,
  headerType: draft.headerType,
  headerContent: draft.headerType === 'TEXT' ? draft.headerContent : undefined,
  headerSample: draft.headerType === 'TEXT' && hasVariable(draft.headerContent)
    ? draft.headerSample
    : undefined,
  headerHandle: MEDIA_HEADER_TYPES.includes(draft.headerType) ? draft.headerHandle : undefined,
  body: draft.body,
  bodySamples: draft.bodySamples,
  variableMapping: draft.variableMapping,
  footer: draft.footer || undefined,
  // `optOut` is the composer's own marker; the server rejects keys it does not know.
  buttons: draft.buttons.map(({ optOut: _optOut, ...button }) => button),
  // Always sent, null included: on an edit the server keeps a key that is ABSENT, so the composer
  // states the link it holds rather than leaving it to be guessed.
  campaignMediaId: draft.campaignMediaId,
  headerAssetUid: ASSET_KIND_FOR_HEADER[draft.headerType] && draft.headerAssetUid ? draft.headerAssetUid : null,
});
